import { supabase } from '../config/supabase';
import logger from './logger';

/**
 * One-time migration script to add existing opened leads (without replies) to automation system
 * Based on campaign status: active campaigns -> sequences, inactive campaigns -> follow-up calls
 */
class ExistingLeadsMigration {
  constructor() {
    this.processedLeads = new Set();
    this.results = {
      totalProcessed: 0,
      addedToSequences: 0,
      addedToFollowUpCalls: 0,
      skipped: 0,
      errors: 0
    };
  }

  /**
   * Main migration function
   */
  async migrate() {
    try {
      logger.log('🚀 Starting migration of existing opened leads...');
      
      // Step 1: Get all leads with open events but no reply events
      const openedLeadsWithoutReplies = await this.getOpenedLeadsWithoutReplies();
      logger.log(`📊 Found ${openedLeadsWithoutReplies.length} leads with opens but no replies`);
      
      // Step 2: Process each lead
      for (const lead of openedLeadsWithoutReplies) {
        await this.processLead(lead);
      }
      
      // Step 3: Show results
      this.showResults();
      
      return this.results;
    } catch (error) {
      logger.error('❌ Migration failed:', error);
      throw error;
    }
  }

  /**
   * Get leads that have open events but no reply events
   */
  async getOpenedLeadsWithoutReplies() {
    try {
      // Get all leads with open events
      const { data: leadsWithOpens, error: opensError } = await supabase
        .from('lead_events')
        .select(`
          lead_email,
          campaign_id,
          campaign_name,
          workspace_id,
          created_at,
          raw_data
        `)
        .eq('event_type', 'open')
        .not('campaign_id', 'is', null)
        .order('created_at', { ascending: false });
      
      if (opensError) throw opensError;
      
      // Get all leads with VALID reply events (include campaign_id for per-campaign filtering)
      // Must match the same logic as activate_campaign_with_sequences RPC:
      //   validation_status = 'valid'
      //   OR (validation_status IS NULL AND reply_text is non-empty)
      // This excludes auto-replies/invalid replies that shouldn't block follow-up
      const { data: leadsWithValidReplies, error: repliesError } = await supabase
        .from('lead_events')
        .select('lead_email, campaign_id, validation_status, reply_text')
        .eq('event_type', 'reply');
      
      if (repliesError) throw repliesError;
      
      // Filter to only VALID replies (matching activate_campaign_with_sequences logic)
      const validReplies = leadsWithValidReplies.filter(r => {
        if (r.validation_status === 'valid') return true;
        if (r.validation_status === 'invalid') return false;
        // validation_status is NULL — treat as valid only if reply_text is non-empty
        return r.reply_text && r.reply_text.trim().length > 0;
      });
      
      // Create set of (lead_email + campaign_id) pairs that have valid replies
      // Per-campaign: a valid reply to Campaign A should NOT block Campaign B
      const repliedPairs = new Set(validReplies.map(r => `${r.lead_email}_${r.campaign_id}`));
      
      // Deduplicate by (lead_email, campaign_id) — one entry per lead per campaign
      // This ensures a lead who opened multiple campaigns gets an entry for EACH campaign
      const uniqueLeads = new Map();
      
      for (const lead of leadsWithOpens) {
        const key = `${lead.lead_email}_${lead.campaign_id}`;
        if (!repliedPairs.has(key) && !uniqueLeads.has(key)) {
          uniqueLeads.set(key, lead);
        }
      }
      
      return Array.from(uniqueLeads.values());
    } catch (error) {
      logger.error('Error getting opened leads without replies:', error);
      throw error;
    }
  }

  /**
   * Process individual lead
   */
  async processLead(leadEvent) {
    try {
      const { lead_email, campaign_id, campaign_name, workspace_id, raw_data } = leadEvent;
      
      // Skip if already processed — track by (lead_email, campaign_id) so
      // the same lead can be processed for different campaigns independently
      const processKey = `${lead_email}_${campaign_id}`;
      if (this.processedLeads.has(processKey)) {
        this.results.skipped++;
        return;
      }
      
      this.processedLeads.add(processKey);
      
      // Get lead details (verify lead exists)
      const { data: lead, error: leadError } = await supabase
        .from('leads')
        .select('*')
        .eq('email', lead_email)
        .maybeSingle();
      
      if (leadError || !lead) {
        logger.warn(`⚠️ Lead not found: ${lead_email}`);
        this.results.errors++;
        return;
      }
      
      // Check if lead already has sequences or follow-up calls
      const hasExistingSequence = await this.checkExistingSequence(lead_email, campaign_id);
      const hasExistingFollowUp = await this.checkExistingFollowUp(lead_email, campaign_id);
      
      if (hasExistingSequence || hasExistingFollowUp) {
        logger.log(`⏭️ Skipping ${lead_email} - already has automation`);
        this.results.skipped++;
        return;
      }
      
      // Check campaign activation status
      const campaignActivation = await this.getCampaignActivation(campaign_id);
      
      if (campaignActivation && campaignActivation.is_active === true) {
        // Add to sequences — pass leadEvent for correct campaign_id
        await this.addToSequence(lead, campaignActivation, raw_data, leadEvent);
        this.results.addedToSequences++;
        logger.log(`✅ Added ${lead_email} to active sequence (campaign: ${campaign_name})`);
      } else {
        // Add to follow-up calls
        await this.addToFollowUpCalls(lead, leadEvent);
        this.results.addedToFollowUpCalls++;
        logger.log(`📞 Added ${lead_email} to follow-up calls (campaign: ${campaign_name})`);
      }
      
      this.results.totalProcessed++;
      
    } catch (error) {
      logger.error(`Error processing lead ${leadEvent.lead_email}:`, error);
      this.results.errors++;
    }
  }

  /**
   * Check if lead already has an active sequence
   */
  async checkExistingSequence(leadEmail, campaignId) {
    const { data, error } = await supabase
      .from('email_automation_sequences')
      .select('id')
      .eq('lead_email', leadEmail)
      .eq('campaign_id', campaignId)
      .in('status', ['active', 'pending'])
      .maybeSingle();
    
    return !error && data;
  }

  /**
   * Check if lead already has a follow-up call entry
   */
  async checkExistingFollowUp(leadEmail, campaignId) {
    const { data, error } = await supabase
      .from('email_automation_followup_calls')
      .select('id')
      .eq('lead_email', leadEmail)
      .eq('campaign_id', campaignId)
      .maybeSingle();
    
    return !error && data;
  }

  /**
   * Get campaign activation details
   */
  async getCampaignActivation(campaignId) {
    const { data, error } = await supabase
      .from('email_automation_campaign_activation')
      .select('*')
      .eq('campaign_id', campaignId)
      .maybeSingle();
    
    return error ? null : data;
  }

  /**
   * Add lead to automation sequence
   */
  async addToSequence(lead, campaignActivation, rawData, leadEvent) {
    const originalInboxEmail = rawData?.email_account || rawData?.sender_email || 'default@otherwiselight.org';
    
    const { error } = await supabase
      .from('email_automation_sequences')
      .insert({
        lead_email: lead.email,
        campaign_id: leadEvent.campaign_id,
        template_id: campaignActivation.template_id,
        current_step: 1,
        status: 'active',
        next_scheduled_at: new Date(Date.now() + ((campaignActivation.sequence_config?.steps?.[0]?.delay_days || 7) * 24 * 60 * 60 * 1000)).toISOString(),
        original_inbox_email: originalInboxEmail,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
    
    if (error) throw error;
  }

  /**
   * Add lead to follow-up calls
   */
  async addToFollowUpCalls(lead, leadEvent) {
    const { error } = await supabase
      .from('email_automation_followup_calls')
      .insert({
        lead_email: lead.email,
        campaign_id: leadEvent.campaign_id,
        campaign_name: leadEvent.campaign_name,
        status: 'pending',
        priority: 'medium',
        open_event_date: leadEvent.created_at,
        original_inbox_email: leadEvent.raw_data?.email_account || leadEvent.raw_data?.sender_email || 'default@otherwiselight.org',
        notes: 'Migrated from existing open events without replies',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
    
    if (error) throw error;
  }

  /**
   * Show migration results
   */
  showResults() {
    logger.log('\n📊 Migration Results:');
    logger.log('='.repeat(50));
    logger.log(`Total Processed: ${this.results.totalProcessed}`);
    logger.log(`Added to Sequences: ${this.results.addedToSequences}`);
    logger.log(`Added to Follow-up Calls: ${this.results.addedToFollowUpCalls}`);
    logger.log(`Skipped (already exists): ${this.results.skipped}`);
    logger.log(`Errors: ${this.results.errors}`);
    logger.log('='.repeat(50));
  }
}

// Export for use
export default ExistingLeadsMigration;

// Function to run migration
export const runMigration = async () => {
  const migration = new ExistingLeadsMigration();
  return await migration.migrate();
};